import {useEffect, useId, useRef} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import {useTranslation} from "../../shared/i18n";


const focusableElements = (container) => Array.from(container.querySelectorAll(
    "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]",
));

export const ReferralDialog = ({open, title, eyebrow, pending = false, onClose, children}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const dialogRef = useRef(null);
    const invokingElementRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        invokingElementRef.current = document.activeElement;
        const frame = window.requestAnimationFrame(() => focusableElements(dialogRef.current)[0]?.focus());
        return () => {
            window.cancelAnimationFrame(frame);
            if (invokingElementRef.current && document.contains(invokingElementRef.current)) {
                invokingElementRef.current.focus();
            }
            invokingElementRef.current = null;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape" && !pending) {
                onClose();
                return;
            }
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = focusableElements(dialogRef.current);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose, open, pending]);

    if (!open) return null;
    return createPortal(
        <div
            className="referral-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !pending) onClose();
            }}
        >
            <section
                ref={dialogRef}
                className="referral-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
            >
                <header className="referral-dialog-header">
                    <div>
                        {eyebrow ? <p>{eyebrow}</p> : null}
                        <h2 id={titleId}>{title}</h2>
                    </div>
                    <button type="button" aria-label={t("sections.referrals.p28")} title={t("sections.referrals.p28")} disabled={pending} onClick={onClose}>
                        <X aria-hidden="true" size={18} />
                    </button>
                </header>
                {children}
            </section>
        </div>,
        document.body,
    );
};

export const ReferralDialogError = ({result}) => result?.message ? (
    <div className="referral-dialog-error" role="alert">{result.message}</div>
) : null;
